//@ts-nocheck

//Components
import ShellLayout from '../../layouts/ShellLayout/ShellLayout';
import shellHeaderBuilder from '../../utils/shellHeaderBuilder';
import TripHeader from '../../components/Trip/TripHeader/TripHeader';
import TripHeaderActions from '../../components/Trip/TripHeaderActions/TripHeaderActions';
import CustomTabs from '../../components/Tabs/CustomTabs';
import CustomTab from '../../components/Tabs/CustomTab';
import ItinerarySkeleton from '../../components/Skeleton/ItinerarySkeleton/ItinerarySkeleton';
import { Suspense, lazy, useEffect, useState } from 'react';

// Lazy load all major components
const Itinerary = lazy(
  () => import('../../components/Trip/Itinerary/Itinerary')
);
const InfoDocs = lazy(() => import('../../components/Trip/InfoDocs/InfoDocs'));
const TripPricing = lazy(
  () => import('../../components/Trip/Pricing/TripPricing/TripPricing')
);
const BookingsPanel = lazy(
  () => import('../../components/Trip/Bookings/BookingsPanel/BookingsPanel')
);
const CustomizeSettings = lazy(
  () => import('../../components/Trip/CustomizeSettings/CustomizeSettings')
);
const PricingPanel = lazy(
  () => import('../../components/Trip/Pricing/PrincingPanel/PrincingPanel')
);
const PassengersPanel = lazy(
  () =>
    import('../../components/Trip/Passengers/PassengersPanel/PassengersPanel')
);
const TripOverview = lazy(
  () => import('../../components/Trip/TripOverview/TripOverview')
);

//Redux Slices
import { createTrip } from '../../redux/slices/trip/trip';
import { updateServices } from '../../redux/slices/services/services';
import { updateStatuses } from '../../redux/slices/tripstatuses/tripstatuses';
import { updateDestinations } from '../../redux/slices/destinations/destinations';
import { addShouldLibraryClose } from '../../redux/slices/library/library';

//Services
import { getTrip } from '../../services/trip/trip';
import { getDestinations } from '../../services/destination/destination';
import { getServiceTypes } from '../../services/servicetype/servicetype';
import { getTripStatuses } from '../../services/tripstatus/tripstatus';
import { getContentTypes } from '../../services/contenttype/contenttype';
import { updateContentTypes } from '../../redux/slices/contenttypes/contenttypes';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Other dependencies
import { useLoaderData, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { XGTabs } from '@xg-cl/xg-cl';
import { useDispatch } from 'react-redux';
import * as PropTypes from 'prop-types';
import './Trip.css';

/**
 * Wraps tab content with Suspense and loading fallback
 * @param {React.ReactNode} children - Tab content
 * @param {boolean} [loading=false] - Loading state
 * @returns {React.ReactElement} Wrapped content
 */
const TabContentWrapper = ({ children, loading = false }) => (
  <Suspense fallback={loading ? <ItinerarySkeleton /> : <></>}>
    {children}
  </Suspense>
);

TabContentWrapper.propTypes = {
  children: PropTypes.node,
  loading: PropTypes.bool
};

/**
 * Renders trip details page with tabs for different views (itinerary, pricing, etc)
 * @param {Object} props - Component props
 * @param {string} props.id - Trip component ID
 * @returns {JSX.Element} Trip page component
 */
const Trip = ({ id }) => {
  const { tripId } = useLoaderData();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation(['trip', 'common']);
  const [isTripLoading, setIsTripLoading] = useState(true);
  const [didTripFetchSucceed, setDidTripFetchSucceed] = useState(true);
  const { tripNavigation } = useFlags();

  if (!didTripFetchSucceed) {
    navigate('/trips');
  }

  const tripTabs = tripNavigation
    ? [
        {
          title: t('overview'),
          content: (
            <TabContentWrapper loading={isTripLoading}>
              {<TripOverview id='trip-overview' />}
            </TabContentWrapper>
          )
        },
        {
          title: t('itinerary'),
          content: (
            <TabContentWrapper loading={isTripLoading}>
              {!isTripLoading && <Itinerary id='trip-itinerary' />}
            </TabContentWrapper>
          )
        },
        {
          title: t('infoAndDocs'),
          content: (
            <TabContentWrapper>
              <InfoDocs id='trip-info-and-docs' />
            </TabContentWrapper>
          )
        },
        {
          title: t('pricing'),
          content: (
            <TabContentWrapper>
              <TripPricing id='trip-pricing-page' />
            </TabContentWrapper>
          )
        },
        {
          title: t('bookings'),
          content: (
            <TabContentWrapper>
              <BookingsPanel key={tripId} />
            </TabContentWrapper>
          )
        },
        {
          title: t('customizedSettings'),
          content: (
            <TabContentWrapper>
              <CustomizeSettings id='trip-customize-settings' />
            </TabContentWrapper>
          )
        }
      ]
    : [
        {
          title: t('itinerary'),
          content: (
            <TabContentWrapper loading={isTripLoading}>
              {!isTripLoading && <Itinerary id='trip-itinerary' />}
            </TabContentWrapper>
          )
        },
        {
          title: t('pricing'),
          content: (
            <TabContentWrapper>
              <PricingPanel id='trip-pricing-panel' />
            </TabContentWrapper>
          )
        },
        {
          title: t('passengers'),
          content: (
            <TabContentWrapper>
              <PassengersPanel />
            </TabContentWrapper>
          )
        },
        {
          title: t('bookings'),
          content: (
            <TabContentWrapper>
              <BookingsPanel key={tripId} />
            </TabContentWrapper>
          )
        }
      ];

  const handleTabChange = () => {
    dispatch(addShouldLibraryClose({ shouldLibraryClose: true }));
  };

  const getTopNavigation = () => {
    return tripNavigation ? (
      <div className='trip-top-navigation'>
        <CustomTabs
          handleTabChange={handleTabChange}
          className='header-nav-tabs'
          activeIndex={1}
        >
          {tripTabs?.map((tab, index) => (
            <CustomTab key={index} label={tab.title}>
              {tab?.content}
            </CustomTab>
          ))}
        </CustomTabs>
      </div>
    ) : (
      <></>
    );
  };

  const mainHeader = shellHeaderBuilder({
    title: <TripHeader id='trip-header' />,
    cta: <TripHeaderActions id='trip-header-actions' />,
    navigation: getTopNavigation()
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tripPromise = getTrip(tripId);
        const destinationsPromise = getDestinations();
        const servicesPromise = getServiceTypes();
        const tripStatusesPromise = getTripStatuses();
        const libraryContentTypesPromise = getContentTypes();

        const [
          tripResult,
          destinationsResult,
          servicesResult,
          tripStatusesResult,
          libraryContentTypesResult
        ] = await Promise.allSettled([
          tripPromise,
          destinationsPromise,
          servicesPromise,
          tripStatusesPromise,
          libraryContentTypesPromise
        ]);

        if (
          tripResult.status === 'fulfilled' &&
          Object.keys(tripResult?.value?.data).length > 0
        ) {
          const tripdays = tripResult.value?.data?.tripday ?? [];
          const tripcontent = tripResult.value?.data?.tripcontent ?? [];

          if (Array.isArray(tripdays)) {
            const newTripdays = tripdays.map((day, index) => ({
              ...day,
              isSelected: index === 0
            }));
            const newTripcontent = tripcontent.map((item, index) => ({
              ...item,
              isSelected: index === 0
            }));

            tripResult.value.data.tripday = newTripdays;
            tripResult.value.data.tripcontent = newTripcontent;

            dispatch(createTrip(tripResult.value.data));
          } else {
            setDidTripFetchSucceed(false);
          }
        } else {
          setDidTripFetchSucceed(false);
        }

        if (destinationsResult.status === 'fulfilled') {
          dispatch(updateDestinations(destinationsResult?.value?.data || []));
        }

        if (servicesResult.status === 'fulfilled') {
          dispatch(updateServices(servicesResult?.value?.data || []));
        }

        if (tripStatusesResult.status === 'fulfilled') {
          dispatch(updateStatuses(tripStatusesResult?.value?.data || []));
        }

        if (libraryContentTypesResult.status === 'fulfilled') {
          dispatch(
            updateContentTypes(libraryContentTypesResult?.value?.data || [])
          );
        }
      } catch (error) {
        setDidTripFetchSucceed(false);
      } finally {
        setIsTripLoading(false);
      }
    };
    fetchData();
  }, [tripId, dispatch]);

  return (
    <div id={id} data-testid='trip-page'>
      <ShellLayout
        id='main-trip-header'
        data-testid='trip-shell'
        headerData={mainHeader}
        isLoadingContent={isTripLoading}
      >
        {!isTripLoading ? (
          !tripNavigation ? (
            <div className='trip-wrapper'>
              <XGTabs
                id='trip-tabs'
                tabs={tripTabs}
                className='trip-tab-panel'
              />
            </div>
          ) : null
        ) : (
          <ItinerarySkeleton id='itinerary-skeleton' />
        )}
      </ShellLayout>
    </div>
  );
};

Trip.propTypes = {
  id: PropTypes.string.isRequired
};

export default Trip;
